import { Stack } from '@mui/joy';
import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { FormProvider, useFormContext, useNotificationContext } from 'core/states';
import { useUpdateCorporatePartPricingType } from 'corporate/hooks';
import { ICorporatePart } from 'corporate/types';
import { PricingType } from 'pricing/enums';
import { Direction } from 'theme/enums';

import { changeToManualSchema, IChangeToManualForm } from './schema';

interface IProps {
  corporatePart: Partial<ICorporatePart>;
  isOpen: boolean;
  onClose: () => void;
}

interface IFieldsProps {
  isOpen: boolean;
  onClose: (reset?: UseFormReset<IChangeToManualForm>) => void;
  onSubmit: (
    reset?: UseFormReset<IChangeToManualForm>
  ) => (data: IChangeToManualForm) => Promise<void>;
}

const ChangeToManualFormFields = ({ isOpen, onClose, onSubmit }: IFieldsProps) => {
  const { t } = useTranslation();
  const { control, errors } = useFormContext();

  return (
    <Form
      title={t('titleChangePricingToManual')}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={onSubmit}
      submitBtnLabel={t('buttonLabelChangePricingToManual')}
    >
      <Stack direction={Direction.ROW} sx={{ gap: 2 }}>
        <FormField
          name="listPrice"
          type={InputType.NUMBER_INPUT}
          inputProps={{
            label: t('labelListPrice'),
            required: true,
            control,
            error: errors?.listPrice,
          }}
        />
        <FormField
          name="listPriceUom"
          type={InputType.TEXT_INPUT}
          inputProps={{
            label: t('labelUom'),
            required: true,
            control,
            error: errors?.listPriceUom,
          }}
        />
      </Stack>
      <Stack direction={Direction.ROW} sx={{ gap: 2 }}>
        <FormField
          name="floorPrice"
          type={InputType.NUMBER_INPUT}
          inputProps={{
            label: t('labelFloorPrice'),
            control,
            error: errors?.floorPrice,
          }}
        />
        <FormField
          name="floorPriceUom"
          type={InputType.TEXT_INPUT}
          inputProps={{
            label: t('labelUom'),
            control,
            error: errors?.floorPriceUom,
          }}
        />
      </Stack>
    </Form>
  );
};

export const ChangeToManualDrawer = ({ corporatePart, isOpen, onClose }: IProps) => {
  const { clearNotification } = useNotificationContext();
  const { updatePricingType } = useUpdateCorporatePartPricingType();

  const defaultValues = useMemo<IChangeToManualForm>(
    () => ({
      listPrice: corporatePart?.originalManualListPrice ?? null,
      listPriceUom: corporatePart?.originalManualListPriceUom || '',
      floorPrice: corporatePart?.originalManualFloorPrice ?? null,
      floorPriceUom: corporatePart?.originalManualFloorPriceUom || '',
    }),
    [corporatePart]
  );

  const handleOnClose = (reset?: UseFormReset<IChangeToManualForm>) => {
    clearNotification();
    onClose();
    reset?.(defaultValues);
  };

  const onSubmit =
    (reset: UseFormReset<IChangeToManualForm>) => async (data: IChangeToManualForm) => {
      const { success } = await updatePricingType({
        corporatePart: {
          id: corporatePart.id,
          pricingType: PricingType.MANUAL,
          listPrice: data.listPrice,
          listPriceUom: data.listPriceUom,
          floorPrice: data.floorPrice,
          floorPriceUom: data.floorPriceUom,
        },
      });

      if (!success) {
        return;
      }

      setTimeout(() => handleOnClose(reset), 1000);
    };

  return (
    <FormProvider schema={changeToManualSchema()} defaultValues={defaultValues} resetDefaultValues>
      <ChangeToManualFormFields isOpen={isOpen} onClose={handleOnClose} onSubmit={onSubmit} />
    </FormProvider>
  );
};
